import { FiltersPersistenceService } from 'src/app/services/filters/filters-persistence.service';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { TuiTime } from '@taiga-ui/cdk';
import {
  debounceTime,
  distinctUntilChanged,
  take,
  catchError,
  of,
  finalize,
  firstValueFrom,
} from 'rxjs';
import { Router, UrlTree } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { HistoryServicesService } from 'src/app/services/history-services/history-services.service';
import { ModalDuplicateServiceComponent } from '../modal-duplicate-service/modal-duplicate-service.component';

import {
  OrderStatusMeta,
  ORDER_STATUS_MAP,
  STATUS_CODE_MAP,
} from 'src/app/constants/order-status';
import { Zone } from 'src/app/models/zone/zone.model';
import { PoolServiceItem } from '../pool-management/interface/pool-service.interface';
import { ServicesService } from 'src/app/services/service/services.service';
import { TuiAlertService } from '@taiga-ui/core';
import { ProductFilterItem } from 'src/app/models/pending-services/pending-services.model';
import { LocalStorageKeys } from 'src/app/enum/local-storage-keys.enum';

type SortDirection = -1 | 0 | 1;

type SortableColumn =
  | 'licensePlate'
  | 'orderHash'
  | 'zoneName'
  | 'assignedStartDate'
  | 'startHour'
  | 'productName'
  | 'status'
  | 'startAddress'
  | 'endAddress'
  | 'clientName'
  | 'driverName'
  | 'totalPrice'
  | 'activeBid'
  | 'hasDeliveryNote';

type TableColumnKey =
  | 'date'
  | 'time'
  | 'licensePlate'
  | 'orderHash'
  | 'zone'
  | 'product'
  | 'client'
  | 'startAddress'
  | 'endAddress'
  | 'status'
  | 'hasDeliveryNote'
  | 'driver'
  | 'totalPrice'
  | 'activeBid'
  | 'actions';

interface ProductOption {
  productName: string;
}
interface ClientOption {
  clientName: string;
  userId?: string;
  businessName?: string;
}
interface DriverOption {
  driverName: string;
}

@Component({
  selector: 'app-services-not-started-table',
  templateUrl: './services-not-started-table.component.html',
  styleUrls: ['./services-not-started-table.component.css'],
})
export class ServicesNotStartedTableComponent implements OnInit, OnDestroy {
  private baselineIndex = new Map<string, number>();
  public loadingEditRows = new Set<string>();

  @Output() refreshRequested = new EventEmitter<void>();

  @Input()
  set data(value: ReadonlyArray<PoolServiceItem> | null) {
    this.allServices = Array.isArray(value) ? [...value] : [];
    this.baselineIndex.clear();
    this.allServices.forEach((s, i) => this.baselineIndex.set(s.orderHash, i));
    this.rebuildLookupsAndRefresh();
  }

  private _isLoading = false;
  isLoadingProducts: boolean = false;

  @Input()
  set isLoading(value: boolean) {
    this._isLoading = value;
    if (value) this.open = false;
    this.toggleFiltersDisabled(value);
  }
  get isLoading(): boolean {
    return this._isLoading;
  }

  index = 0;
  length = 0;
  readonly pageSize = 25;
  open = false;

  private allServices: PoolServiceItem[] = [];
  paginatedData: PoolServiceItem[] = [];

  private sortDirection: SortDirection = -1;
  private sortedColumn: SortableColumn | '' = '';

  readonly sizes = ['l', 'm', 's'] as const;
  size = this.sizes[0];

  exportCsv = false;
  exportXlsx = false;

  readonly columns: ReadonlyArray<TableColumnKey> = [
    'date',
    'time',
    'licensePlate',
    'orderHash',
    'zone',
    'product',
    'client',
    'startAddress',
    'endAddress',
    'status',
    'hasDeliveryNote',
    'driver',
    'totalPrice',
    'activeBid',
    'actions',
  ];

  zones: Zone[] = [];
  products: ProductFilterItem[] = [];
  allClients: ClientOption[] = [];
  allDrivers: DriverOption[] = [];

  readonly orderStatus = Object.entries(STATUS_CODE_MAP).map(([code, key]) => ({
    nodeStatus: Number(code),
    statusName: ORDER_STATUS_MAP[key]?.label ?? 'Desconocido',
  }));

  filtersForm: FormGroup = this.fb.group({
    search: ['' as string],
    dateRange: [null as [Date, Date] | null],
    zones: [[] as string[] | null],
    products: [[] as string[]],
    clients: [[] as string[]],
    drivers: [[] as string[]],
    status: [[] as number[]],
    startTime: [null as TuiTime | null],
    endTime: [null as TuiTime | null],
  });

  startTimeItems: TuiTime[] = this.generateTimeItems();
  endTimeItems: TuiTime[] = [];

  @Output() countEvent = new EventEmitter<number>();

  constructor(
    private readonly historyServicesSvc: HistoryServicesService,
    private readonly offcanvasService: NgbOffcanvas,
    private readonly router: Router,
    private readonly translate: TranslateService,
    private readonly fb: FormBuilder,
    private readonly servicesSvc: ServicesService,
    private readonly tuiAlertService: TuiAlertService,
    private readonly filtersPersistence: FiltersPersistenceService
  ) {}

  ngOnInit(): void {
    this.loadZones();
    this.restoreFilters();
    this.setupFormSubscriptions();
    this.toggleFiltersDisabled(this.isLoading);
    this.applyAllFiltersAndPaginate();
    this.setupFiltersPersistence();
  }

  ngOnDestroy(): void {
    this.filtersPersistence.clear(LocalStorageKeys.SERVICESNOTSTARTEDFILTERS);
  }

  private setupFiltersPersistence(): void {
    this.filtersForm.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => {
        this.persistFilters();
      });
  }

  private persistFilters(): void {
    const values = this.filtersForm.value;
    const params = {
      search: typeof values.search === 'string' ? values.search : '',
      dateRange: Array.isArray(values.dateRange) ? values.dateRange : null,
      zones: Array.isArray(values.zones) ? values.zones : [],
      products: Array.isArray(values.products) ? values.products : [],
      clients: Array.isArray(values.clients) ? values.clients : [],
      drivers: Array.isArray(values.drivers) ? values.drivers : [],
      status: Array.isArray(values.status) ? values.status : [],
      startTime: values.startTime ?? null,
      endTime: values.endTime ?? null
    };
    this.filtersPersistence.save(LocalStorageKeys.SERVICESNOTSTARTEDFILTERS, params);
  }

  private restoreFilters(): void {
    const defaultFilters = {
      search: '',
      dateRange: null,
      zones: [],
      products: [],
      clients: [],
      drivers: [],
      status: [],
      startTime: null,
      endTime: null
    };
    const filters = this.filtersPersistence.restore(LocalStorageKeys.SERVICESNOTSTARTEDFILTERS, defaultFilters);
    this.filtersForm.patchValue(filters, { emitEvent: false });
  }

  private loadZones(): void {
    this.servicesSvc.getZones().subscribe({
      next: (res) => {
        this.zones = res.zones;
      },
      error: (err) => {
        this.tuiAlertService
          .open(
            this.translate.instant(
              'services_not_started_table.zones_load_error_message'
            ),
            {
              label: this.translate.instant(
                'services_not_started_table.zones_load_error_title'
              ),
              status: 'error',
              autoClose: true,
            }
          )
          .subscribe();
      },
    });
  }

  private rebuildLookupsAndRefresh(): void {
    // this.buildProducts();
    this.buildAllClients();
    this.buildAllDrivers();
    this.index = 0;
    this.applyAllFiltersAndPaginate();
  }

  // private buildProducts(): void {
  //   const unique = new Set(
  //     this.allServices
  //       .map((s) => s.productName)
  //       .filter(
  //         (p): p is string => typeof p === 'string' && p.trim().length > 0
  //       )
  //   );
  //   this.products = Array.from(unique)
  //     .sort()
  //     .map((productName) => ({ productName }));
  // }

  private buildAllClients(): void {
    const unique = new Set(
      this.allServices.map((s) => s.clientName).filter((n) => !!n)
    );
    this.allClients = Array.from(unique)
      .sort()
      .map((clientName) => ({ clientName }));
  }

  private buildFilename(
    baseKey: string,
    date: string,
    ext: 'csv' | 'xlsx'
  ): string {
    const raw = this.translate.instant(baseKey, { date });

    const withoutExt = raw.replace(/\.(csv|xlsx|xls)$/i, '');

    const sanitized = withoutExt
      .replace(/[<>:"\\|?*]+/g, '')
      .replace(/[\/]+/g, '-')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\s/g, '-');

    return `${sanitized}.${ext}`;
  }

  private buildAllDrivers(): void {
    const unassigned = this.translate.instant(
      'services_not_started_table.unassigned'
    );
    const unique = new Set(
      this.allServices
        .map((s) => (s.driverName ?? '').trim())
        .filter((n) => n.length > 0)
    );
    this.allDrivers = [
      { driverName: unassigned },
      ...Array.from(unique)
        .sort()
        .map((driverName) => ({ driverName })),
    ];
  }

  private setupFormSubscriptions(): void {
    this.filtersForm
      .get('startTime')
      ?.valueChanges.subscribe((startTime: TuiTime | null) => {
        if (startTime) {
          this.endTimeItems = this.generateTimeItems().filter(
            (t) =>
              t.toAbsoluteMilliseconds() > startTime.toAbsoluteMilliseconds()
          );
          const first = this.endTimeItems[0] ?? null;
          this.filtersForm
            .get('endTime')
            ?.setValue(first, { emitEvent: false });
        } else {
          this.endTimeItems = this.generateTimeItems();
          this.filtersForm.get('endTime')?.setValue(null, { emitEvent: false });
        }
        this.filterDataBySelect(0);
      });

    this.filtersForm.get('endTime')?.valueChanges.subscribe(() => {
      this.filterDataBySelect(0);
    });

    this.filtersForm
      .get('search')
      ?.valueChanges.pipe(debounceTime(200), distinctUntilChanged())
      .subscribe(() => this.filterData());

    (
      [
        'dateRange',
        'zones',
        'products',
        'clients',
        'drivers',
        'status',
      ] as const
    ).forEach((ctrl) =>
      this.filtersForm
        .get(ctrl)
        ?.valueChanges.subscribe(() => this.filterDataBySelect(0))
    );
  }

  async getProductsFilter(): Promise<void> {
    this.isLoadingProducts = true;
    this.products = await firstValueFrom(
      this.historyServicesSvc.getProductsForFilters()
    );

    this.products = this.products.map(
      (product: ProductFilterItem): ProductFilterItem => {
        product.ProductKey = this.translateProductKey(product.ProductKey);
        return product;
      }
    );
    this.isLoadingProducts = false;
  }

  filterData(): void {
    this.index = 0;
    this.applyAllFiltersAndPaginate();
  }

  filterDataBySelect(index?: number): void {
    this.index = index ?? 0;
    this.applyAllFiltersAndPaginate();
  }

  private applyAllFiltersAndPaginate(): void {
    let data = [...this.allServices];

    data = this.applySearchFilter(data);
    data = this.applyClientFilter(data);
    data = this.applyZoneFilter(data);
    data = this.applyProductFilter(data);
    data = this.applyDateFilter(data);
    data = this.applyTimeFilter(data);
    data = this.applyStatusFilter(data);
    data = this.applyDriverFilter(data);

    data = this.sortBy(data);
    this.paginateAndEmit(data);
  }

  private applySearchFilter(data: PoolServiceItem[]): PoolServiceItem[] {
    const termCtrl = this.filtersForm.get('search')?.value;
    const term = (termCtrl ?? '').toString().trim().toLowerCase();
    if (!term) return data;

    const unassigned = this.translate
      .instant('services_not_started_table.unassigned')
      .toLowerCase();

    return data.filter((item) => {
      const fields = [
        item.licensePlate?.toLowerCase(),
        item.orderHash?.toLowerCase(),
        (item.driverName?.trim() || unassigned).toLowerCase(),
        item.clientName?.toLowerCase(),
      ];
      return fields.some((f) => f && f.includes(term));
    });
  }

  trackByOrderHash = (_: number, s: { orderHash: string }) => s.orderHash;

  private applyClientFilter(data: PoolServiceItem[]): PoolServiceItem[] {
    const selected: string[] =
      this.filtersForm.get('clients')?.value ?? ([] as string[]);
    if (!selected.length) return data;
    const set = new Set(selected);
    return data.filter((item) => set.has(item.clientName));
  }

  private applyZoneFilter(data: PoolServiceItem[]): PoolServiceItem[] {
    const zones: string[] | null = this.filtersForm.get('zones')?.value ?? null;
    if (!zones?.length) return data;
    return data.filter((item) => zones.includes(item.zoneName));
  }

  private applyProductFilter(data: PoolServiceItem[]): PoolServiceItem[] {
    const selected: string[] =
      this.filtersForm.get('products')?.value ?? ([] as string[]);
    if (!selected.length) return data;
    const set = new Set(selected);
    return data.filter((item) =>
      item.productName ? set.has(item.productName) : false
    );
  }

  private applyDateFilter(data: PoolServiceItem[]): PoolServiceItem[] {
    const range = this.filtersForm.get('dateRange')?.value as
      | [Date, Date]
      | null;
    if (!range) return data;

    const [start, end] = range;
    const startMs = new Date(start).setHours(0, 0, 0, 0);
    const endMs = new Date(end).setHours(23, 59, 59, 999);

    return data.filter((s) => {
      const startIso = (s.assignedStartDate ?? '').split(' ')[0];
      const t = new Date(startIso).getTime();
      return !Number.isNaN(t) && t >= startMs && t <= endMs;
    });
  }

  private applyTimeFilter(data: PoolServiceItem[]): PoolServiceItem[] {
    const s: TuiTime | null = this.filtersForm.get('startTime')?.value ?? null;
    const e: TuiTime | null = this.filtersForm.get('endTime')?.value ?? null;
    if (!s || !e) return data;

    const startMinutes = s.hours * 60 + s.minutes;
    const endMinutes = e.hours * 60 + e.minutes;

    return data.filter((item) => {
      const startIso = (item.assignedStartDate ?? '').split(' ')[0];
      const endIso = (item.endDate ?? '').split(' ')[0];

      const startDate = new Date(startIso);
      const endDate = new Date(endIso);
      if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime()))
        return false;

      const itemStartMins = startDate.getHours() * 60 + startDate.getMinutes();
      const itemEndMins = endDate.getHours() * 60 + endDate.getMinutes();

      return itemStartMins >= startMinutes && itemEndMins <= endMinutes;
    });
  }

  private applyStatusFilter(data: PoolServiceItem[]): PoolServiceItem[] {
    const selected: number[] =
      this.filtersForm.get('status')?.value ?? ([] as number[]);
    if (!selected.length) return data;
    const set = new Set(selected);
    return data.filter((item) => set.has(item.nodeStatus));
  }

  private applyDriverFilter(data: PoolServiceItem[]): PoolServiceItem[] {
    const selected: string[] =
      this.filtersForm.get('drivers')?.value ?? ([] as string[]);
    if (!selected.length) return data;

    const unassigned = this.translate.instant(
      'services_not_started_table.unassigned'
    );
    const set = new Set(selected);

    return data.filter((item) => {
      const name = (item.driverName ?? '').trim();
      const isUnassigned = !name && set.has(unassigned);
      const isAssigned = name && set.has(name);
      return isUnassigned || isAssigned;
    });
  }

  setSortColumn(column: SortableColumn): void {
    if (this.sortedColumn !== column) {
      this.sortedColumn = column;
      this.sortDirection = 1;
    } else {
      this.sortDirection =
        this.sortDirection === 1 ? -1 : this.sortDirection === -1 ? 0 : 1;

      if (this.sortDirection === 0) {
        this.sortedColumn = '';
      }
    }

    this.applyAllFiltersAndPaginate();
  }

  onSort(dir: SortDirection): void {
    this.sortDirection = dir;
    this.applyAllFiltersAndPaginate();
  }

  private sortBy(data: PoolServiceItem[]): PoolServiceItem[] {
    // Sin columna o en estado ORIGINAL => por orden de llegada (baselineIndex)
    if (!this.sortedColumn || this.sortDirection === 0) {
      return [...data].sort(
        (a, b) =>
          (this.baselineIndex.get(a.orderHash) ?? 0) -
          (this.baselineIndex.get(b.orderHash) ?? 0)
      );
    }

    type Comparable = number | string;
    const col = this.sortedColumn;
    const dir = this.sortDirection; // 1 asc, -1 desc

    const getValue = (item: PoolServiceItem): Comparable => {
      switch (col) {
        case 'assignedStartDate': {
          const t = Date.parse((item.assignedStartDate ?? '').split(' ')[0]);
          return Number.isNaN(t) ? 0 : t;
        }
        case 'startHour': {
          const d = new Date((item.assignedStartDate ?? '').split(' ')[0]);
          return Number.isNaN(d.getTime())
            ? 0
            : d.getHours() * 60 + d.getMinutes();
        }
        case 'status':
          return item.nodeStatus ?? 0;
        case 'totalPrice':
          return item.totalPrice ?? 0;
        case 'activeBid':
          return item.userBidCount ?? 0;
        case 'productName':
          return item.productName ?? '';
        case 'startAddress':
          return item.startAddress ?? '';
        case 'endAddress':
          return item.endAddress ?? '';
        case 'licensePlate':
          return item.licensePlate ?? '';
        case 'orderHash':
          return item.orderHash ?? '';
        case 'zoneName':
          return item.zoneName ?? '';
        case 'clientName':
          return item.clientName ?? '';
        case 'driverName':
          return item.driverName ?? '';
        default:
          return '';
      }
    };

    const cmp = (a: Comparable, b: Comparable): number => {
      if (typeof a === 'string' && typeof b === 'string') {
        return a.localeCompare(b, 'es', { sensitivity: 'base' });
      }
      return Number(a) - Number(b);
    };

    return [...data].sort((a, b) => {
      const A = getValue(a);
      const B = getValue(b);
      const res = cmp(A, B);
      return dir === 1 ? res : -res;
    });
  }

  private paginateAndEmit(data: PoolServiceItem[]): void {
    this.length = Math.max(1, Math.ceil(data.length / this.pageSize));
    if (this.index >= this.length) this.index = 0;

    const start = this.index * this.pageSize;
    this.paginatedData = data.slice(start, start + this.pageSize);

    this.countEvent.emit(data.length);
  }

  goToPage(pageIndex: number): void {
    this.index = pageIndex;
    this.applyAllFiltersAndPaginate();
  }

  private generateTimeItems(): TuiTime[] {
    const items: TuiTime[] = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 15) {
        items.push(new TuiTime(h, m));
      }
    }
    return items;
  }

  formatHourRange(start: string, end: string): string {
    const toDate = (s: string): Date => new Date((s ?? '').split(' ')[0]);
    const format = (d: Date): string =>
      d.toLocaleTimeString('es-ES', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });

    const s = toDate(start);
    const e = toDate(end);
    return `${format(s)}-${format(e)}`;
  }

  hoursUntil(dateStr: string): number {
    const iso = (dateStr ?? '').split(' ')[0];
    const target = new Date(iso).getTime();
    if (Number.isNaN(target)) return 0;
    const diffMs = target - Date.now();
    return diffMs > 0 ? Math.ceil(diffMs / 36e5) : 0;
  }

  formatDate(dateStr: string): string {
    const d = new Date((dateStr ?? '').split(' ')[0]);
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    }).format(d);
  }

  isWithin48Hours(dateStr: string, driverName: string): boolean {
    const d = new Date((dateStr ?? '').split(' ')[0]);
    const now = new Date();
    const diffHours = (d.getTime() - now.getTime()) / (1000 * 60 * 60);
    const withinNext48 = diffHours <= 48 && diffHours > 0;
    const inPast = d < now;
    return (withinNext48 || inPast) && !driverName;
  }

  getOrderStatusMeta(statusCode: number): OrderStatusMeta {
    const key = STATUS_CODE_MAP[statusCode];
    return (
      ORDER_STATUS_MAP[key] ?? {
        label: 'Desconocido',
        tuiStatus: 'neutral',
        cssClass: '',
      }
    );
  }

  translateProduct(name: string): string {
    if (!name)
      return this.translate.instant('services_not_started_table.no_product');
    const key = 'common.services.' + name;
    const translated = this.translate.instant(key);
    return translated === key ? name : translated;
  }

  getClientById(_id?: string): string | null {
    return null;
  }

  openModalEditService(service: PoolServiceItem): void {
    const hash = service.orderHash;
    if (!hash) return;

    this.loadingEditRows.add(hash);

    this.historyServicesSvc
      .getServiceDetail(hash)
      .pipe(
        take(1),
        catchError((err) => {
          this.tuiAlertService
            .open(
              this.translate.instant(
                'services_not_started_table.error_loading_detail'
              ),
              {
                label: this.translate.instant(
                  'services_not_started_table.error_title'
                ),
                status: 'error',
                autoClose: true,
              }
            )
            .subscribe();
          return of(null);
        }),
        finalize(() => this.loadingEditRows.delete(hash))
      )
      .subscribe((serviceInfo) => {
        if (!serviceInfo) return;

        const ref = this.offcanvasService.open(ModalDuplicateServiceComponent, {
          backdrop: 'static',
          position: 'end',
        });
        ref.componentInstance.type_info = 'ed';
        ref.componentInstance.service_info = serviceInfo;

        if ('finished' in ref.componentInstance) {
          (ref.componentInstance as any).finished
            .pipe(take(1))
            .subscribe((ok: boolean) => {
              if (ok) this.refreshRequested.emit();
            });
        }
      });
  }

  isRowLoading(orderHash: string | null | undefined): boolean {
    return !!orderHash && this.loadingEditRows.has(orderHash);
  }

  viewDetail({ orderHash }: { orderHash: string }): void {
    this.historyServicesSvc.setData({ orderHash });
    const tree: UrlTree = this.router.createUrlTree(
      ['/service-manager/detail-service'],
      { queryParams: { orderHash } }
    );
    window.open(`${window.location.origin}/#/${tree}`, '_blank');
  }

  exportSelected(): void {
    if (this.exportCsv) this.exportCSV();
    if (this.exportXlsx) this.exportXLSX();
  }

  private exportCSV(): void {
    const t = this.translate;
    const today = this.safeDateForFilename(new Date(), 'Europe/Madrid');
    const filename = this.buildFilename(
      'services_not_started_table.export_filename_service_not_start',
      today,
      'csv'
    );

    const headers = [
      t.instant('services_not_started_table.plate_input_table'),
      t.instant('services_not_started_table.hour_input_table'),
      t.instant('services_not_started_table.hash_input_table'),
      t.instant('services_not_started_table.zone_input_table'),
      t.instant('services_not_started_table.date_input_table'),
      t.instant('services_not_started_table.product_input_table'),
      t.instant('services_not_started_table.status_input_table'),
      t.instant('services_not_started_table.client_input_table'),
      t.instant('services_not_started_table.column_driver'),
      t.instant('services_not_started_table.column_price'),
      t.instant('services_not_started_table.column_price_without_vat'),
      t.instant('services_not_started_table.column_pool_price'),
      t.instant('services_not_started_table.column_active_bid'),
    ].map((h) => this.escapeCsv(h));

    const rows = this.allServices.map((r) =>
      [
        r.licensePlate,
        this.formatHourRange(r.assignedStartDate, r.endDate),
        r.orderHash,
        r.zoneName,
        this.formatDate(r.assignedStartDate),
        this.translateProduct(r.productName ?? ''),
        t.instant(this.getOrderStatusMeta(r.nodeStatus).label),
        r.clientName,
        r.driverName || t.instant('services_not_started_table.unassigned'),
        Number(r.totalPrice ?? 0).toFixed(2),
        Number(r.priceWithoutTax ?? 0).toFixed(2),
        Number(r.poolPrice ?? 0).toFixed(2),
        String(r.userBidCount ?? 0),
      ].map((v) => this.escapeCsv(String(v)))
    );

    const csv = [headers, ...rows].map((l) => l.join(';')).join('\n');
    saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), filename);
  }

  private exportXLSX(): void {
    const t = this.translate;
    const today = this.safeDateForFilename(new Date(), 'Europe/Madrid');
    const filename = this.buildFilename(
      'services_not_started_table.export_filename_service_not_start',
      today,
      'xlsx'
    );

    const headers = [
      t.instant('services_not_started_table.plate_input_table'),
      t.instant('services_not_started_table.hour_input_table'),
      t.instant('services_not_started_table.hash_input_table'),
      t.instant('services_not_started_table.zone_input_table'),
      t.instant('services_not_started_table.date_input_table'),
      t.instant('services_not_started_table.product_input_table'),
      t.instant('services_not_started_table.status_input_table'),
      t.instant('services_not_started_table.client_input_table'),
      t.instant('services_not_started_table.column_driver'),
      t.instant('services_not_started_table.column_price'),
      t.instant('services_not_started_table.column_price_without_vat'),
      t.instant('services_not_started_table.column_pool_price'),
      t.instant('services_not_started_table.column_active_bid'),
    ];

    const rows = this.allServices.map((r) => [
      r.licensePlate,
      this.formatHourRange(r.assignedStartDate, r.endDate),
      r.orderHash,
      r.zoneName,
      this.formatDate(r.assignedStartDate),
      this.translateProduct(r.productName ?? ''),
      t.instant(this.getOrderStatusMeta(r.nodeStatus).label),
      r.clientName,
      r.driverName || t.instant('services_not_started_table.unassigned'),
      Number(r.totalPrice ?? 0).toFixed(2),
      Number(r.priceWithoutTax ?? 0).toFixed(2),
      Number(r.poolPrice ?? 0).toFixed(2),
      String(r.userBidCount ?? 0),
    ]);

    const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Pendientes');
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([wbout]), filename);
  }

  private safeDateForFilename(d: Date, timeZone: string): string {
    const fmt = new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone,
    });
    return fmt.format(d).replace(/\//g, '-');
  }

  private escapeCsv(value: string): string {
    return /[",\n;]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
  }

  private isBlank(value: unknown): boolean {
    return (
      value === null ||
      value === undefined ||
      (typeof value === 'string' && value.trim() === '')
    );
  }

  display(value: unknown): string {
    return this.isBlank(value) ? '-' : String(value);
  }

  private toggleFiltersDisabled(loading: boolean): void {
    if (!this.filtersForm) return;
    if (loading) {
      this.filtersForm.disable({ emitEvent: false });
    } else {
      this.filtersForm.enable({ emitEvent: false });
    }
  }

  translateProductKey(name: string): string {
    return this.historyServicesSvc.productsCodes[this.translate.currentLang][
      name
    ];
  }
}
